import { useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import cloneDeep from 'lodash.clonedeep';
import { styled } from '@mui/material/styles';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Button from '@components/Button';
import { SortableItem, DroppableEmptyZone } from '@components/DragAndDrop';
import AddIcon from '@mui/icons-material/AddRounded';
import { useTranslation } from '@hooks';
import { createSection, createItem, getLocalizedValue, buildSectionAiContext } from '../helpers';
import SectionDivider from './SectionDivider';
import MenuItemCard from './MenuItemCard';
import MenuLocalizeModal from './MenuLocalizeModal';

const AddRow = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    margin: '8px 0 16px',
});

const Builder = ({ isView, defaultLanguage }) => {
    const { setValue, watch } = useFormContext();
    const { t } = useTranslation();
    const [localizeSectionId, setLocalizeSectionId] = useState(null);

    const document = watch('document');
    const currencyCode = watch('currencyCode');
    const menuId = watch('id');
    const sections = document?.sections || [];

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    const updateSections = newSections => {
        setValue('document', { ...document, sections: newSections }, { shouldDirty: true });
    };

    const onAddSection = () => {
        const section = createSection(defaultLanguage);
        updateSections([...sections, section]);
        setLocalizeSectionId(section.id);
    };

    const onUpdateSection = (sectionId, updater) => {
        const copy = cloneDeep(sections);
        const index = copy.findIndex(s => s.id === sectionId);
        if (index === -1) return;
        copy[index] = updater(copy[index]);
        updateSections(copy);
    };

    const onDeleteSection = sectionId => {
        updateSections(sections.filter(s => s.id !== sectionId));
    };

    const onMoveSection = (sectionId, direction) => {
        const index = sections.findIndex(s => s.id === sectionId);
        const target = index + direction;
        if (index === -1 || target < 0 || target >= sections.length) return;
        const copy = [...sections];
        [copy[index], copy[target]] = [copy[target], copy[index]];
        updateSections(copy);
    };

    const onAddItem = sectionId => {
        const targetId = sectionId ?? sections[sections.length - 1].id;
        onUpdateSection(targetId, current => ({
            ...current,
            items: [...(current.items || []), createItem(defaultLanguage)],
        }));
    };

    const onUpdateItem = (sectionId, itemId, updater) => {
        onUpdateSection(sectionId, current => {
            const items = cloneDeep(current.items || []);
            const index = items.findIndex(i => i.id === itemId);
            if (index !== -1) {
                items[index] = updater(items[index]);
            }
            return { ...current, items };
        });
    };

    const onDeleteItem = (sectionId, itemId) => {
        onUpdateSection(sectionId, current => ({
            ...current,
            items: (current.items || []).filter(i => i.id !== itemId),
        }));
    };

    const preDragSnapshot = useRef(null);

    const findSectionIdForId = id => {
        if (typeof id === 'string' && id.startsWith('section-')) {
            return id.replace('section-', '');
        }
        return sections.find(s => (s.items || []).some(i => i.id === id))?.id;
    };

    const handleDragStart = () => {
        preDragSnapshot.current = sections;
    };

    const handleDragCancel = () => {
        if (preDragSnapshot.current) {
            updateSections(preDragSnapshot.current);
            preDragSnapshot.current = null;
        }
    };

    const handleDragOver = ({ active, over }) => {
        if (!over) return;
        const activeSectionId = findSectionIdForId(active.id);
        const overSectionId = findSectionIdForId(String(over.id));
        if (!activeSectionId || !overSectionId || activeSectionId === overSectionId) return;

        let moved;
        const next = sections.map(section => {
            if (section.id !== activeSectionId) return section;
            const items = section.items || [];
            moved = items.find(i => i.id === active.id);
            return { ...section, items: items.filter(i => i.id !== active.id) };
        });
        if (!moved) return;
        updateSections(
            next.map(section =>
                section.id === overSectionId ? { ...section, items: [...(section.items || []), moved] } : section,
            ),
        );
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over) {
            handleDragCancel();
            return;
        }
        preDragSnapshot.current = null;
        if (active.id === over.id) return;

        const sectionId = findSectionIdForId(active.id);
        const overSectionId = findSectionIdForId(String(over.id));
        if (!sectionId || sectionId !== overSectionId) return;

        updateSections(
            sections.map(section => {
                if (section.id !== sectionId) return section;
                const items = [...(section.items || [])];
                const oldIndex = items.findIndex(i => i.id === active.id);
                const newIndex = items.findIndex(i => i.id === over.id);
                if (oldIndex === -1 || newIndex === -1) return section;
                const [moved] = items.splice(oldIndex, 1);
                items.splice(newIndex, 0, moved);
                return { ...section, items };
            }),
        );
    };

    const localizeSection = sections.find(s => s.id === localizeSectionId);

    return (
        <div>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {sections.map((section, index) => {
                    const items = section.items || [];
                    const isLast = index === sections.length - 1;
                    return (
                        <div key={section.id}>
                            <SectionDivider
                                section={section}
                                isFirst={index === 0}
                                isLast={isLast}
                                isView={isView}
                                defaultLanguage={defaultLanguage}
                                onEdit={() => setLocalizeSectionId(section.id)}
                                onDelete={() => onDeleteSection(section.id)}
                                onMove={direction => onMoveSection(section.id, direction)}
                            />
                            {items.length ? (
                                <SortableContext
                                    id={`section-${section.id}`}
                                    items={items.map(i => i.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div>
                                        {items.map(item => (
                                            <SortableItem key={item.id} id={item.id}>
                                                <MenuItemCard
                                                    isView={isView}
                                                    item={item}
                                                    menuId={menuId}
                                                    currencyCode={currencyCode}
                                                    defaultLanguage={defaultLanguage}
                                                    onUpdate={updater => onUpdateItem(section.id, item.id, updater)}
                                                    onDelete={() => onDeleteItem(section.id, item.id)}
                                                />
                                            </SortableItem>
                                        ))}
                                    </div>
                                </SortableContext>
                            ) : (
                                <DroppableEmptyZone
                                    id={`section-${section.id}`}
                                    label={t('MenusPage.noItems')}
                                    style={{ marginBottom: 8 }}
                                />
                            )}
                            {!isView && !isLast ? (
                                <AddRow>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => onAddItem(section.id)}
                                    >
                                        {t('MenusPage.addItem')}
                                    </Button>
                                </AddRow>
                            ) : null}
                        </div>
                    );
                })}
            </DndContext>
            {!isView ? (
                <AddRow>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => onAddItem()}>
                        {t('MenusPage.addItem')}
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onAddSection}>
                        {t('MenusPage.addSection')}
                    </Button>
                </AddRow>
            ) : null}
            {localizeSection ? (
                <MenuLocalizeModal
                    isOpen
                    title={`${t('MenusPage.editSection')} - ${
                        getLocalizedValue(localizeSection.names, defaultLanguage) || t('MenusPage.untitledSection')
                    }`}
                    names={localizeSection.names || []}
                    descriptions={localizeSection.descriptions || []}
                    aiContext={buildSectionAiContext(localizeSection, defaultLanguage)}
                    defaultLanguage={defaultLanguage}
                    onClose={() => setLocalizeSectionId(null)}
                    onSave={(names, descriptions) => {
                        onUpdateSection(localizeSection.id, current => ({ ...current, names, descriptions }));
                        setLocalizeSectionId(null);
                    }}
                />
            ) : null}
        </div>
    );
};

export default Builder;
